// One conversation: its stored messages, the turn in flight, and the actions on it.
//
// A turn in flight is shown from its live events. When the server says it is stored
// (`saved`), the conversation is re-read, so what stays on screen is exactly what a reload
// would show — there is never a second, browser-only version of an answer.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatEvent, ConversationDetailsDto } from "../../types/index.ts";

export type SaveEvent = Extract<ChatEvent, { type: "save" }>;
import type { PreviewEvent } from "../preview-panel/index.ts";
import { fetchConversation, followTurn, sendMessage, stopTurn } from "../../integrations/api.ts";
import { ApiError } from "../../integrations/http.ts";
import { isLimitRefusal } from "../limit-lock/index.ts";
import { applyEvent, startTurn, type LiveTurn } from "./turn-state.ts";

export function useConversation(
  id: string | null,
  onTurnFinished: () => void,
  onPreviewEvent: (event: PreviewEvent) => void = () => {},
  /** The model the person has chosen, read when a message is sent. */
  model: string | null = null,
  /** Called when a send is refused because the monthly limit is reached. */
  onLimitReached: () => void = () => {},
  /** Whether what the agent changed was saved — the project's, like the preview. */
  onSaveEvent: (event: SaveEvent) => void = () => {},
) {
  const chosenModel = useRef(model);
  chosenModel.current = model;
  const [conversation, setConversation] = useState<ConversationDetailsDto | null>(null);
  const [live, setLive] = useState<LiveTurn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const abort = useRef<AbortController | null>(null);
  /** A first message waiting for its brand-new conversation to be opened. */
  const pending = useRef<{ id: string; text: string } | null>(null);
  const finished = useRef(onTurnFinished);
  finished.current = onTurnFinished;
  const previewed = useRef(onPreviewEvent);
  previewed.current = onPreviewEvent;
  const limited = useRef(onLimitReached);
  limited.current = onLimitReached;
  const saved = useRef(onSaveEvent);
  saved.current = onSaveEvent;

  const consume = useCallback(async (conversationId: string, events: AsyncGenerator<ChatEvent>) => {
    try {
      for await (const event of events) {
        setLive((turn) => (turn ? applyEvent(turn, event) : turn));
        // The preview is the project's, not the conversation's — the workspace owns it.
        if (event.type === "preview") previewed.current(event);
        if (event.type === "save") saved.current(event);
        if (event.type === "saved") break;
      }
    } catch (e) {
      // The limit has its own notice; the generic error would say the same thing twice.
      if (isLimitRefusal(e)) limited.current();
      else if (!(e instanceof DOMException && e.name === "AbortError")) {
        setError(e instanceof ApiError ? e.message : "Anslutningen bröts. Svaret fortsätter i bakgrunden — ladda om om en stund.");
      }
    }
    try {
      setConversation(await fetchConversation(conversationId));
    } finally {
      setLive(null);
      finished.current();
    }
  }, []);

  useEffect(() => {
    abort.current?.abort();
    setConversation(null);
    setLive(null);
    setError(null);
    if (!id) return;

    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    fetchConversation(id)
      .then((details) => {
        if (controller.signal.aborted) return;
        setConversation(details);
        if (pending.current?.id === id) {
          const { text } = pending.current;
          pending.current = null;
          void send(text, id);
          return;
        }
        // Still running — after a reload, or started in another tab. Follow it.
        if (details.busy) {
          setLive(startTurn(""));
          void consume(id, followTurn(id, controller.signal));
        }
      })
      .catch((e) => !controller.signal.aborted && setError(e instanceof ApiError ? e.message : "Kunde inte läsa konversationen."))
      .finally(() => !controller.signal.aborted && setLoading(false));
    return () => controller.abort();
    // `send` is stable for a given id; listing it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, consume]);

  const queueFirstMessage = useCallback((conversationId: string, text: string) => {
    pending.current = { id: conversationId, text };
  }, []);

  const send = useCallback(
    async (text: string, targetId: string = id ?? "") => {
      if (!targetId) return;
      setError(null);
      setLive(startTurn(text));
      const controller = new AbortController();
      abort.current = controller;
      try {
        await consume(targetId, sendMessage(targetId, text, chosenModel.current, controller.signal));
      } catch (e) {
        setLive(null);
        setError(e instanceof ApiError ? e.message : "Meddelandet kunde inte skickas.");
      }
    },
    [id, consume],
  );

  const stop = useCallback(async () => {
    if (id) await stopTurn(id).catch(() => undefined);
  }, [id]);

  return { conversation, live, error, loading, send, stop, queueFirstMessage, dismissError: () => setError(null) };
}
